/**
 * The Academy: tracks of short lessons about the markets and how to trade them, each ending on the
 * platform's own screens and numbers.
 * Content lives in ./tracks (one file per track); this file puts it in order and holds the helpers
 * the web app and the API share (lookup, placeholders, quiz check, recommended path).
 */
import { REWARD_TIERS } from '../rewards.js';
import { calendarNews, calendarNewsTerms } from './tracks/calendar-news.js';
import { commodities, commoditiesTerms } from './tracks/commodities.js';
import { cryptoMarkets, cryptoMarketsTerms } from './tracks/crypto-markets.js';
import { firstTrade, firstTradeTerms } from './tracks/first-trade.js';
import { forex, forexTerms } from './tracks/forex.js';
import { howMarketsWork, howMarketsWorkTerms } from './tracks/how-markets-work.js';
import { leverageMargin, leverageMarginTerms } from './tracks/leverage-margin.js';
import { priceCharts, priceChartsTerms } from './tracks/price-charts.js';
import { psychology, psychologyTerms } from './tracks/psychology.js';
import { riskManagement, riskManagementTerms } from './tracks/risk-management.js';
import { sentiment, sentimentTerms } from './tracks/sentiment.js';
import { stocksIndices, stocksIndicesTerms } from './tracks/stocks-indices.js';
import { strategies, strategiesTerms } from './tracks/strategies.js';
import { technicalAnalysis, technicalAnalysisTerms } from './tracks/technical-analysis.js';
import { ACADEMY_VAR_DEFAULTS } from './types.js';
export * from './types.js';
/** In the order they are shown: beginner, intermediate, then advanced. */
export const ACADEMY_TRACKS = [
    howMarketsWork,
    leverageMargin,
    firstTrade,
    priceCharts,
    cryptoMarkets,
    forex,
    commodities,
    stocksIndices,
    calendarNews,
    technicalAnalysis,
    riskManagement,
    strategies,
    sentiment,
    psychology,
];
/** Academy home hero and the "Track complete" card. */
export const ACADEMY_HERO_IMAGE = { src: '/academy/hero.webp', alt: 'Bitcoin, Ether, gold and dollar coins floating over a dark floor' };
export const ACADEMY_DONE_IMAGE = { src: '/academy/done.webp', alt: 'A gold coin above stacks of gold coins' };
/** Glossary, A–Z. */
export const ACADEMY_TERMS = [
    ...howMarketsWorkTerms,
    ...leverageMarginTerms,
    ...firstTradeTerms,
    ...priceChartsTerms,
    ...cryptoMarketsTerms,
    ...forexTerms,
    ...commoditiesTerms,
    ...stocksIndicesTerms,
    ...calendarNewsTerms,
    ...technicalAnalysisTerms,
    ...riskManagementTerms,
    ...strategiesTerms,
    ...sentimentTerms,
    ...psychologyTerms,
].sort((a, b) => a.term.localeCompare(b.term, 'en', { sensitivity: 'base' }));
const LESSONS = new Map();
for (const track of ACADEMY_TRACKS)
    track.lessons.forEach((lesson, index) => LESSONS.set(lesson.id, { lesson, track, index }));
export const academyTrack = (id) => ACADEMY_TRACKS.find((t) => t.id === id) ?? null;
export const academyLesson = (id) => LESSONS.get(id) ?? null;
export const academyTerm = (id) => ACADEMY_TERMS.find((t) => t.id === id) ?? null;
export const academyLessonCount = () => LESSONS.size;
/** Lesson URL. */
export const lessonPath = (lessonId) => {
    const l = LESSONS.get(lessonId);
    return l ? `/academy/${l.track.id}/${lessonId}` : '/academy/tracks';
};
/** Tracks a client sees: Crypto Pro's only when Crypto Pro is shown to them. */
export const visibleTracks = (opts) => ACADEMY_TRACKS.filter((t) => t.requires !== 'pro' || opts.pro);
/** Replace `{name}` placeholders (Academy and staff guide vars); unknown names are left as written. */
export function fillVars(text, vars = {}) {
    return text.replace(/\{([a-zA-Z0-9]+)\}/g, (m, k) => k in vars ? String(vars[k]) : k in ACADEMY_VAR_DEFAULTS ? ACADEMY_VAR_DEFAULTS[k] : m);
}
/** True when every quiz question is answered with its right option. */
export function quizPassed(lesson, answers) {
    return Array.isArray(answers) && answers.length === lesson.quiz.length && lesson.quiz.every((q, i) => answers[i] === q.answer);
}
/** Lessons done per track, and whether the track is finished. */
export function trackProgress(track, done) {
    const completed = track.lessons.filter((l) => done.has(l.id)).length;
    return { completed, total: track.lessons.length, finished: completed === track.lessons.length };
}
/** Tracks every beginner should finish (the "All beginner tracks" quest). Crypto Pro is never one. */
export const BEGINNER_TRACKS = ACADEMY_TRACKS.filter((t) => t.level === 'beginner' && !t.requires).map((t) => t.id);
/**
 * Suggested order of tracks for each Earn tier: the content gets deeper as the client trades more.
 * Tiers are admin-editable, so a tier not listed here uses the path of the built-in tier at the
 * same position (or the last one).
 */
export const TIER_PATHS = {
    wood: ['how-markets-work', 'leverage-margin', 'first-trade', 'price-charts'],
    bronze: ['risk-management', 'crypto-markets', 'calendar-news', 'price-charts'],
    silver: ['technical-analysis', 'forex', 'commodities', 'stocks-indices'],
    gold: ['strategies', 'sentiment', 'psychology', 'calendar-news'],
    platinum: ['strategies', 'sentiment', 'psychology'],
    diamond: ['psychology', 'strategies', 'sentiment'],
    champion: ['psychology', 'risk-management', 'sentiment'],
};
/** Short description of each built-in tier's path, for the Academy home. */
export const TIER_PATH_NOTES = {
    wood: 'The basics: how markets work, leverage and margin, the trade screen and reading a price chart.',
    bronze: 'Protect your account and learn your market: risk, crypto and the economic calendar.',
    silver: 'Widen your view: technical analysis, forex, commodities, stocks and indices.',
    gold: 'Put it together: strategies, sentiment and the psychology of trading.',
    platinum: 'Sharpen your edge: strategies, positioning and discipline.',
    diamond: 'Stay disciplined: psychology first, then strategy and positioning.',
    champion: 'Stay sharp: psychology, risk and positioning.',
};
export function tierPath(tierId, tiers = REWARD_TIERS) {
    if (TIER_PATHS[tierId])
        return TIER_PATHS[tierId];
    const i = Math.max(0, tiers.findIndex((t) => t.id === tierId));
    const builtIn = REWARD_TIERS[Math.min(i, REWARD_TIERS.length - 1)];
    return TIER_PATHS[builtIn.id];
}
export function tierPathNote(tierId, tiers = REWARD_TIERS) {
    if (TIER_PATH_NOTES[tierId])
        return TIER_PATH_NOTES[tierId];
    const i = Math.max(0, tiers.findIndex((t) => t.id === tierId));
    return TIER_PATH_NOTES[REWARD_TIERS[Math.min(i, REWARD_TIERS.length - 1)].id];
}
/**
 * The next lesson to suggest: the first unfinished lesson on the tier's path, then the first
 * unfinished lesson anywhere. Null when everything visible is done.
 */
export function nextLesson(done, opts) {
    const visible = visibleTracks(opts);
    const path = tierPath(opts.tierId, opts.tiers)
        .map((id) => visible.find((t) => t.id === id))
        .filter((t) => !!t);
    for (const t of [...path, ...visible]) {
        const l = t.lessons.find((x) => !done.has(x.id));
        if (l)
            return l;
    }
    return null;
}
//# sourceMappingURL=index.js.map